/**
 * PRP-IMPORT-PRICENOTE-1 item 6: pick a price note for a line from the user's list, or
 * remove it. The list loads when the picker opens. `lines` is plural because the PATCH
 * marks lines in one call; the tables have no multi-select today, so each caller passes one.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { PriceNotesAPI, type PriceNoteLine } from '../../lib/priceNotesApi';
import { cn } from '../../lib/utils';
import type { ParsedEstimateResponse } from '../../types';
import { IconTag } from './icons';
import { Button } from './ui/button';
import { Modal } from './ui/modal';

export interface PriceNotePickerProps {
  parseHash: string;
  lines: PriceNoteLine[];
  /** The note on the line now, if any. */
  current?: string | null;
  /** The line's code, e.g. "DRY 1/2". */
  code: string;
  description?: string;
  onResult: (_res: ParsedEstimateResponse) => void;
}

const message = (err: unknown) =>
  err instanceof Error ? err.message : 'The price note could not be saved. Try again.';

function PriceNoteDialog({
  parseHash,
  lines,
  current,
  code,
  description,
  onResult,
  onClose,
}: PriceNotePickerProps & { onClose: () => void }) {
  const [notes, setNotes] = useState<string[] | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    PriceNotesAPI.list()
      .then((r) => {
        if (!cancelled) setNotes(r.notes ?? []);
      })
      .catch((err) => {
        if (!cancelled) {
          setNotes([]);
          setError(message(err));
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const save = async (note: string) => {
    setSaving(true);
    setError(null);
    try {
      onResult(await PriceNotesAPI.mark(parseHash, lines, note));
      onClose();
    } catch (err) {
      setError(message(err));
      setSaving(false);
    }
  };

  return (
    <Modal
      label="Price note"
      title="Price note"
      subtitle={`${code} ${description ?? ''}`.trim()}
      onClose={onClose}
      className="max-w-[420px]"
      testId="price-note-dialog"
      footer={
        <>
          <Link
            to="/profile?tab=price-notes"
            className="mr-auto self-center text-[12px] text-[var(--accent-ink)] hover:underline"
          >
            Change my list
          </Link>
          {current && (
            <Button size="sm" variant="danger" disabled={saving} onClick={() => void save('')}>
              Remove price note
            </Button>
          )}
        </>
      }
    >
      {error && (
        <p role="alert" className="mt-0 mb-3 text-[12.5px] text-[var(--bad)]">
          {error}
        </p>
      )}
      {notes === null ? (
        <p className="m-0 text-[12.5px] text-[var(--tx-lo)]">Loading your list…</p>
      ) : (
        <div className="grid grid-cols-2 gap-[6px]" role="group" aria-label="Your price notes">
          {notes.map((note) => (
            <button
              key={note}
              type="button"
              disabled={saving}
              aria-pressed={note === current}
              onClick={() => void save(note)}
              className={cn(
                'min-h-[38px] px-3 py-[6px] text-left text-[12.5px] rounded-[var(--r-sm)] border border-[var(--bd)] bg-[var(--bg-2)] text-[var(--tx-hi)] hover:border-[var(--accent)] hover:bg-[var(--bg-3)] disabled:opacity-60',
                note === current && 'border-[var(--accent)] bg-[var(--accent-soft)]',
              )}
            >
              {note}
            </button>
          ))}
        </div>
      )}
    </Modal>
  );
}

/** The tag button on a line, and its dialog. */
export default function PriceNotePicker(props: PriceNotePickerProps) {
  const [open, setOpen] = useState(false);
  const { current, code } = props;
  return (
    // Rows toggle on click; nothing in here should reach them.
    <span className="inline-flex" onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        aria-label={`Price note for ${code}`}
        title={current ? `Price note: ${current}` : 'Add a price note'}
        className={cn(
          'relative w-[26px] h-[26px] grid place-items-center rounded-[var(--r-sm)] border border-transparent bg-transparent',
          'text-[var(--tx-faint)] hover:text-[var(--tx-mid)] hover:bg-[var(--bg-3)]',
          current &&
            "text-[var(--accent-ink)] after:content-[''] after:absolute after:top-1 after:right-1 after:w-[5px] after:h-[5px] after:rounded-full after:bg-[var(--accent-ink)]",
        )}
        onClick={() => setOpen(true)}
      >
        <IconTag className="w-[14px] h-[14px]" />
      </button>
      {open && <PriceNoteDialog {...props} onClose={() => setOpen(false)} />}
    </span>
  );
}
